import { useCallback, useEffect, useMemo, useState } from "react";
import { useStableCallback } from "../../hooks/useStableCallback";
import { assert } from "../../utils/assert";
import { DATA_ATTRIBUTE_LIST_INDEX } from "./List";
import type { DynamicRowHeight } from "./types";

export function useDynamicRowHeight({
  defaultRowHeight,
  key
}: {
  defaultRowHeight: number;
  key?: string | number;
}) {
  const [state, setState] = useState<{
    key: string | number | undefined;
    map: Map<number, number>;
  }>({
    key,
    map: new Map()
  });

  if (state.key !== key) {
    setState({
      key,
      map: new Map()
    });
  }

  const { map } = state;

  const getAverageRowHeight = useCallback(() => {
    let totalHeight = 0;

    map.forEach((height) => {
      totalHeight += height;
    });

    if (totalHeight === 0) {
      return defaultRowHeight;
    }

    return totalHeight / map.size;
  }, [defaultRowHeight, map]);

  const getRowHeight = useCallback(
    (index: number) => {
      const measuredHeight = map.get(index);
      if (measuredHeight !== undefined) {
        return measuredHeight;
      }

      // Keep estimates stable when rowProps change (see issue #863), but do not
      // store them as measurements or include them in the measured average.
      return defaultRowHeight;
    },
    [defaultRowHeight, map]
  );

  const setRowHeight = useCallback((index: number, size: number) => {
    setState((prevState) => {
      if (prevState.map.get(index) === size) {
        return prevState;
      }

      const clonedMap = new Map(prevState.map);
      clonedMap.set(index, size);

      return {
        ...prevState,
        map: clonedMap
      };
    });
  }, []);

  const resizeObserverCallback = useStableCallback(
    (entries: ResizeObserverEntry[]) => {
      if (entries.length === 0) {
        return;
      }

      entries.forEach((entry) => {
        const { borderBoxSize, target } = entry;

        const attribute = target.getAttribute(DATA_ATTRIBUTE_LIST_INDEX);
        assert(
          attribute !== null,
          `Invalid ${DATA_ATTRIBUTE_LIST_INDEX} attribute value`
        );

        const index = parseInt(attribute);

        const { blockSize: height } = borderBoxSize[0];
        if (!height) {
          // Ignore heights that have not yet been measured (e.g. <img> elements that have not yet loaded)
          return;
        }

        setRowHeight(index, height);
      });
    }
  );

  const [resizeObserver] = useState(() => {
    if (typeof ResizeObserver !== "undefined") {
      return new ResizeObserver(resizeObserverCallback);
    }
  });

  useEffect(() => {
    if (resizeObserver) {
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [resizeObserver]);

  const observeRowElements = useCallback(
    (elements: Element[] | NodeListOf<Element>) => {
      if (resizeObserver) {
        elements.forEach((element) => resizeObserver.observe(element));
        return () => {
          elements.forEach((element) => resizeObserver.unobserve(element));
        };
      }
      return () => {};
    },
    [resizeObserver]
  );

  return useMemo<DynamicRowHeight>(
    () => ({
      getAverageRowHeight,
      getRowHeight,
      setRowHeight,
      observeRowElements
    }),
    [getAverageRowHeight, getRowHeight, setRowHeight, observeRowElements]
  );
}
